import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Card,
  Stack,
  Chip,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { ArrowBack as ArrowBackIcon, InboxOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { queryCouponList } from '../../api/modules/invite';
import dayjs from 'dayjs';
import { showMessage } from '../../utils';
import { isInApp } from '../../utils/environment';
const VerificationHistory = () => {
  const [orders, setOrders] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrders();
  }, [startDate, endDate]);

  const fetchOrders = async () => {
    try {
      const params = {
        bizType: 1,
        ...(startDate && { startTime: dayjs(startDate).format('YYYY-MM-DD') }),
        ...(endDate && { endTime: dayjs(endDate).format('YYYY-MM-DD') })
      };
      
      await queryCouponList(params, (rst) => {
        const { code, rows } = rst;
        if (code === 200 && rows) {
          setOrders(rows);
        }
      });
    } catch (error) {
      showMessage('Failed to fetch orders', 'error');
    }
  };

  // 添加空状态组件
  const EmptyState = () => (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: 'white',
      borderRadius: 2,
      py: 8,
      px: 2
    }}>
      <InboxOutlined sx={{ 
        fontSize: 48,
        color: '#9CA3AF',
        mb: 2
      }} />
      <Typography sx={{ 
        color: '#111111',
        fontSize: '1rem',
        fontWeight: 500,
        mb: 1
      }}>
        No Verification History
      </Typography>
      <Typography sx={{ 
        color: '#6B7280',
        fontSize: '0.875rem',
        textAlign: 'center'
      }}>
        No verification records found for the selected period
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ bgcolor: '#F8F9FA', minHeight: '100vh' }}>
      {/* Header */}
      {
        !isInApp() && ( 
          <Box sx={{ 
            bgcolor: 'white',
            borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
            position: 'sticky',
            top: 0,
            zIndex: 10,
        px: 2,
        py: 1.5
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1
        }}>
          <IconButton 
            onClick={() => navigate(-1)}
            edge="start"
            sx={{ color: '#111111' }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography sx={{ 
            fontSize: '1.25rem',
            fontWeight: 600,
            color: '#111111',
            lineHeight: 1
          }}>
            Verification History
          </Typography>
        </Box>
      </Box>
        )
      }

      {/* Filter Section */}
      <Box sx={{ p: 2, bgcolor: 'white', mb: 1.5 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{ 
            display: 'flex', 
            gap: 2,
            mt: 2
          }}>
            <DesktopDatePicker
              label="Start Date"
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              slotProps={{ 
                textField: { 
                  fullWidth: true,
                  sx: {
                    '& .MuiOutlinedInput-root': {
                      height: '48px',
                      borderRadius: '12px',
                      bgcolor: '#fff',
                      '& fieldset': {
                        borderColor: '#E5E7EB'
                      },
                      '&:hover fieldset': {
                        borderColor: '#E5E7EB'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#000'
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: '#6B7280',
                      '&.Mui-focused': {
                        color: '#000'
                      }
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.9375rem',
                      color: '#111111',
                      '&::placeholder': {
                        color: '#9CA3AF',
                        opacity: 1
                      }
                    }
                  }
                },
                inputAdornment: {
                  sx: {
                    '& .MuiIconButton-root': {
                      color: '#6B7280'
                    }
                  }
                }
              }}
            />
            <DesktopDatePicker
              label="End Date"
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              slotProps={{ 
                textField: { 
                  fullWidth: true,
                  sx: {
                    '& .MuiOutlinedInput-root': {
                      height: '48px',
                      borderRadius: '12px',
                      bgcolor: '#fff',
                      '& fieldset': {
                        borderColor: '#E5E7EB'
                      },
                      '&:hover fieldset': {
                        borderColor: '#E5E7EB'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#000'
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: '#6B7280',
                      '&.Mui-focused': {
                        color: '#000'
                      }
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.9375rem',
                      color: '#111111',
                      '&::placeholder': {
                        color: '#9CA3AF',
                        opacity: 1
                      }
                    }
                  }
                },
                inputAdornment: {
                  sx: {
                    '& .MuiIconButton-root': {
                      color: '#6B7280'
                    }
                  }
                }
              }}
            />
          </Box>
        </LocalizationProvider>
      </Box>

      {/* Order List */}
      <Stack spacing={1.5} sx={{ p: 2 }}>
        {orders.length > 0 ? (
          orders.map((order) => (
            <Card
              key={order.couponId}
              elevation={0}
              sx={{
                bgcolor: 'white',
                borderRadius: 2,
                p: 2.5,
                border: '1px solid',
                borderColor: 'rgba(0, 0, 0, 0.06)'
              }}
            >
              <Box sx={{ 
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2
              }}>
                <Typography sx={{ 
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: '#111111'
                }}>
                  Order #{order.couponId}
                </Typography>
                <Chip 
                  label={order.status === 1 ? 'Success' : 'Failed'}
                  sx={{ 
                    height: 24,
                    borderRadius: '100px',
                    bgcolor: order.status === 1 ? '#DCFCE7' : '#FEE2E2',
                    color: order.status === 1 ? '#166534' : '#991B1B',
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    '& .MuiChip-label': {
                      px: 1.5
                    }
                  }}
                />
              </Box>

              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: 1
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between'
                }}>
                  <Typography sx={{ 
                    color: '#6B7280',
                    fontSize: '0.9375rem'
                  }}>
                    Amount
                  </Typography>
                  <Typography sx={{ 
                    color: '#111111',
                    fontWeight: 500,
                    fontSize: '0.9375rem'
                  }}>
                    ${order.actualPrice}
                  </Typography>
                </Box>

                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between'
                }}>
                  <Typography sx={{ 
                    color: '#6B7280',
                    fontSize: '0.9375rem'
                  }}>
                    Method
                  </Typography>
                  <Typography sx={{ 
                    color: '#111111',
                    fontWeight: 500,
                    fontSize: '0.9375rem'
                  }}>
                    {order.verifyType === 1 ? 'QR Code' : 'Manual'}
                  </Typography>
                </Box>
              </Box>

              <Typography sx={{ 
                color: '#6B7280',
                fontSize: '0.875rem',
                mt: 2
              }}>
                {order.createTime}
              </Typography>
            </Card>
          ))
        ) : (
          <EmptyState />
        )}
      </Stack>
    </Box>
  );
};

export default VerificationHistory;
